import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiClock, FiCheckCircle, FiAlertCircle, FiInfo, FiUpload, FiPackage, FiSearch, FiDatabase, FiSave } from 'react-icons/fi';

const TaskLogContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  padding: 0 0 2em 0;
  min-width: 300px;
  width: 100%;
`;

const ProgressSection = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 0.6em;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .stage-text {
    font-size: 0.75em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
  }
  
  .progress-percent {
    font-size: 1.5em;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    line-height: 1.2;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 2px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ $progress }) => $progress || 0}%;
    background: ${({ theme }) => theme.primary};
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

const StatsGrid = styled.div`
  display: flex;
  gap: 0.8em;
  flex: 1;
  margin-bottom: 0.5em;
`;

const StatCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 14px 12px;
  min-height: 65px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  min-width: 100px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }
  
  .stat-icon {
    width: 32px;
    height: 32px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    flex-shrink: 0;
    
    &.info {
      background: rgba(59, 130, 246, 0.12);
      color: #3b82f6;
    }
    &.success {
      background: rgba(16, 185, 129, 0.12);
      color: #10b981;
    }
    &.error {
      background: rgba(239, 68, 68, 0.12);
      color: #ef4444;
    }
    &.warning {
      background: rgba(245, 158, 11, 0.12);
      color: #f59e0b;
    }
  }
  
  .stat-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }
  
  .stat-value {
    font-size: 1.35em;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    line-height: 1.1;
    margin: 0;
  }
  
  .stat-label {
    font-size: 0.68em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    margin: 0;
    line-height: 1.2;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const LogsSection = styled.div`
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.02)" : "rgba(255, 255, 255, 0.02)"};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 6px;
  padding: 0.8em;
  max-height: 200px;
  display: flex;
  flex-direction: column;
`;

const LogsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.5em 0.6em;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  flex-shrink: 0;
  
  svg {
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
  }
  
  span {
    font-size: 0.75em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    font-family: 'Courier New', monospace;
  }
`;

const LogsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5em;
  display: flex;
  flex-direction: column;
  gap: 0.3em;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 2px;
  }
`;

const LogEntry = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  padding: 0.4em 0.5em;
  border-radius: 3px;
  background: transparent;
  
  &:last-child {
    border-bottom: none;
  }
  
  .log-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    margin-top: 0.2em;
    background: ${({ type, theme }) => {
      if (type === 'success') return 'rgba(16, 185, 129, 0.15)';
      if (type === 'error') return 'rgba(239, 68, 68, 0.15)';
      if (type === 'progress') return `${theme.primary}20`;
      return theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)";
    }};
    
    svg {
      font-size: 0.75em;
      color: ${({ type, theme }) => {
        if (type === 'success') return '#10b981';
        if (type === 'error') return '#ef4444';
        if (type === 'progress') return theme.primary;
        return theme.text;
      }};
      opacity: ${({ type }) => type === 'info' ? 0.6 : 1};
    }
  }
  
  .log-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.1em;
    
    .log-time {
      font-size: 0.65em;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      font-family: 'Courier New', monospace;
    }
    
    .log-message {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      line-height: 1.3;
      font-family: 'Courier New', monospace;
      opacity: 0.8;
    }
    
    .log-progress-bar {
      width: 100%;
      height: 4px;
      background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.08)"};
      border-radius: 2px;
      overflow: hidden;
      margin-top: 0.3em;
      
      .progress-fill {
        height: 100%;
        background: ${({ theme }) => theme.primary};
        transition: width 0.3s ease;
        border-radius: 2px;
      }
    }
  }
`;

const DownloadButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1em 0 0 0;
  
  button {
    min-width: 200px;
    padding: 12px 24px;
    background: ${({ theme }) => theme.primary};
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 0.9em;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    
    &:hover:not(:disabled) {
      opacity: 0.9;
      transform: translateY(-1px);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &:disabled {
      cursor: wait;
      opacity: 1;
    }
    
    svg {
      font-size: 1em;
      
      &.spinner {
        animation: spin 1s linear infinite;
      }
    }
  }
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Componente de progresso unificado para Download XMLs e XML Scan
 * 
 * @param {Object} props
 * @param {boolean} props.isProcessing - Se está processando
 * @param {boolean} props.processingComplete - Se completou
 * @param {number} props.taskProgress - Progresso (0-100)
 * @param {string} props.currentStage - Estágio atual
 * @param {Object} props.taskStats - Estatísticas {totalChaves, arquivosEncontrados, arquivosFaltantes} ou {totalXmls, conformes, parciais, naoConformes}
 * @param {Array} props.taskLogs - Logs [{message, type, timestamp, progress}]
 * @param {React.Component} props.children - Botões de download ou ações
 * @param {string} props.completionTitle - Título ao completar
 * @param {string} props.completionMessage - Mensagem ao completar
 */
export default function UnifiedTaskProgress({ 
  isProcessing, 
  processingComplete, 
  taskProgress, 
  currentStage, 
  taskStats = {}, 
  taskLogs = [],
  children,
  completionTitle = 'Processamento Concluído!',
  completionMessage = 'Os arquivos estão prontos'
}) {
  const [aggregatedLogs, setAggregatedLogs] = useState([]);
  const logsContentRef = useRef(null);

  // Aglutinar logs similares
  useEffect(() => {
    if (taskLogs.length === 0) {
      setAggregatedLogs([]);
      return;
    }

    // Agrupar logs por tipo de mensagem
    const logGroups = {};
    
    taskLogs.forEach((log) => {
      // Extrair tipo de ação da mensagem (primeira palavra significativa)
      let actionType = 'default';
      const msg = log.message || '';
      
      if (msg.includes('Extraindo') || msg.includes('Extração')) {
        actionType = 'extraction';
      } else if (msg.includes('Analisando') || msg.includes('Análise')) {
        actionType = 'analysis';
      } else if (msg.includes('Processando') || msg.includes('Processamento')) {
        actionType = 'processing';
      } else if (msg.includes('Validando') || msg.includes('Validação')) {
        actionType = 'validation';
      } else if (msg.includes('Agregando') || msg.includes('Agregação')) {
        actionType = 'aggregation';
      } else if (msg.includes('Salvando') || msg.includes('Gravando')) {
        actionType = 'saving';
      } else if (msg.includes('Erro') || msg.includes('Falha')) {
        actionType = `error_${Date.now()}`; // Erros sempre separados
      } else if (msg.includes('Concluí') || msg.includes('sucesso')) {
        actionType = `success_${Date.now()}`; // Sucessos sempre separados
      } else {
        actionType = `info_${Date.now()}`; // Outros sempre separados
      }
      
      // Agrupar por tipo de ação
      if (!logGroups[actionType]) {
        logGroups[actionType] = [];
      }
      logGroups[actionType].push(log);
    });

    // Pegar apenas o último log de cada grupo
    const newAggregated = Object.values(logGroups).map(group => {
      const lastLog = group[group.length - 1];
      return {
        ...lastLog,
        count: group.length // Quantas vezes apareceu
      };
    });

    // Inverter para mostrar última mensagem primeiro
    setAggregatedLogs(newAggregated.reverse());
  }, [taskLogs]);

  // Scroll automático para o topo (onde está a última mensagem)
  useEffect(() => {
    if (logsContentRef.current) {
      logsContentRef.current.scrollTop = 0;
    }
  }, [aggregatedLogs]);

  const getLogIcon = (type, message = '') => {
    // Ícones específicos baseados no conteúdo da mensagem
    if (type === 'success') return <FiCheckCircle />;
    if (type === 'error') return <FiAlertCircle />;
    
    // Ícones específicos para diferentes etapas
    if (message.includes('Upload') || message.includes('Enviando')) return <FiUpload />;
    if (message.includes('Extraindo') || message.includes('Extração')) return <FiPackage />;
    if (message.includes('Analisando') || message.includes('Análise')) return <FiSearch />;
    if (message.includes('Agregando') || message.includes('Agregação')) return <FiDatabase />;
    if (message.includes('Salvando') || message.includes('Salvamento')) return <FiSave />;
    
    // Padrão para progress
    if (type === 'progress') return <FiClock />;
    
    return <FiInfo />;
  };

  // Normalizar stats (pode vir como array ou objeto)
  let stats = [];
  
  if (Array.isArray(taskStats)) {
    // Já vem como array do Worker
    stats = taskStats;
  } else if (taskStats && typeof taskStats === 'object') {
    // Detectar tipo de stats (Download ou Scan)
    const isDownloadMode = 'totalChaves' in taskStats;
    const isScanMode = 'totalXmls' in taskStats;

    stats = isDownloadMode ? [
      { label: 'Total Solicitadas', value: taskStats.totalChaves || 0, type: 'info' },
      { label: 'Faltantes', value: taskStats.arquivosFaltantes || 0, type: 'error' }
    ] : isScanMode ? [
      { label: 'Total XMLs', value: taskStats.totalXmls || 0 },
      { label: 'Conformes', value: taskStats.conformes || 0, type: 'success' },
      { label: 'Parciais', value: taskStats.parciais || 0, type: 'warning' },
      { label: 'Não Conformes', value: taskStats.naoConformes || 0, type: 'error' }
    ] : [];
  }

  return (
    <TaskLogContainer>
      {/* KPIs sempre visíveis - estilo ECAC */}
      {stats.length > 0 && (
        <StatsGrid>
          {stats.map((stat, index) => {
            // Definir ícone baseado no tipo
            let IconComponent = FiInfo;
            if (stat.type === 'success') IconComponent = FiCheckCircle;
            if (stat.type === 'error') IconComponent = FiAlertCircle;
            if (stat.type === 'warning') IconComponent = FiClock;
            
            return (
              <StatCard key={index}>
                <div className={`stat-icon ${stat.type || 'info'}`}>
                  <IconComponent />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    {typeof stat.value === 'string' ? stat.value : stat.value.toLocaleString('pt-BR')}
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </StatCard>
            );
          })}
        </StatsGrid>
      )}

      {/* Seção de extração e barra de progresso - sempre visível */}
      <ProgressSection>
        <ProgressHeader>
          <span className="stage-text">{currentStage || 'Iniciando...'}</span>
          <span className="progress-percent">{Math.round(taskProgress || 0)}%</span>
        </ProgressHeader>
        <ProgressBar $progress={taskProgress || 0} />
      </ProgressSection>

      {/* Seção de logs */}
      <LogsSection>
        <LogsHeader>
          <FiClock />
          <span>Log de Atividades</span>
        </LogsHeader>
        <LogsContent ref={logsContentRef}>
          {aggregatedLogs.length === 0 ? (
            <LogEntry type="info">
              <div className="log-icon">
                <FiInfo />
              </div>
              <div className="log-content">
                <span className="log-time">{new Date().toLocaleTimeString()}</span>
                <span className="log-message">Aguardando início do processamento...</span>
              </div>
            </LogEntry>
          ) : (
            aggregatedLogs.map((log, index) => {
              // Calcular progresso baseado no count ou progress
              const progressValue = log.progress !== undefined 
                ? log.progress 
                : (log.count > 1 ? 100 : 0); // Se apareceu múltiplas vezes, mostra 100%
              
              return (
                <LogEntry key={index} type={log.type}>
                  <div className="log-icon">
                    {getLogIcon(log.type, log.message)}
                  </div>
                  <div className="log-content">
                    <span className="log-time">{log.timestamp}</span>
                    <span className="log-message">
                      {log.message}
                      {log.count > 1 && ` (${log.count}x)`}
                    </span>
                    {progressValue > 0 && (
                      <div className="log-progress-bar">
                        <div className="progress-fill" style={{ width: `${progressValue}%` }} />
                      </div>
                    )}
                  </div>
                </LogEntry>
              );
            })
          )}
        </LogsContent>
      </LogsSection>

      {/* Botão de download centralizado quando concluído */}
      {processingComplete && children && (
        <DownloadButtonContainer>
          {children}
        </DownloadButtonContainer>
      )}
    </TaskLogContainer>
  );
}
